<script setup>
import { computed, reactive, ref } from 'vue';
import AccountManagerComponent from '@components-molecules/AccountManager.vue';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import Slidein from '@components/Slidein.vue';
import { trans } from '@elements/translation';
import { Logger } from '@tools/logger/logger';
import InvitedEmailTag from '../InvitedEmailTag.vue';
import { useOrganizationStore } from '../../../store/organization';

const store = useOrganizationStore();

const { emailsToInvite, accountManager } = store;
const email = ref();
const inputEmail = ref();
const tagSelectedIndex = ref();

const modal = reactive({
  isOpened: false,
  isCheckingEmail: false,
  isAlreadyMember: false,
  isInActiveOrganization: false,
  isSubmitting: false,
});

const input = reactive({
  isHovered: false,
  isFocus: false,
});

function openModal() {
  modal.isOpened = true;
}

function validateEmail(emailToValidate) {
  /**
   * Email validation regex - accepts "+" symbol for Google domain accounts.
   * @link https://regex101.com/r/4qtQ7P/5
   */
  const regex = /^\w+([\+\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/i; // eslint-disable-line no-useless-escape
  return regex.test(emailToValidate);
}

async function existenceCheck(emailToTest, index) {
  modal.isCheckingEmail = true;

  try {
    const data = await store.isInvitedUserAlreadyMember(emailToTest);

    if (!data) {
      return;
    }

    if (data.exists === true) {
      store.setEmailError(index);
      modal.isAlreadyMember = true;
    }

    if (data.active === true) {
      store.setEmailError(index);
      modal.isInActiveOrganization = true;
    }
  } catch (e) {
    flashBagError();
    Logger.error('Error catched', e);
  } finally {
    modal.isCheckingEmail = false;
  }
}

function addEmail(emailToAdd) {
  if (validateEmail(emailToAdd) && !store.isEmailAlreadyInvited(emailToAdd)) {
    const length = store.addEmail(emailToAdd);
    tagSelectedIndex.value = null;

    email.value = null;

    existenceCheck(emailToAdd, length - 1);
  }
}

function onEmailAdd() {
  if (!email.value) {
    return;
  }

  const currentEmail = email.value.trim();
  const space = ' ';

  if (currentEmail.includes(space)) {
    const emails = currentEmail.split(space);
    emails.forEach((emailToAdd) => {
      addEmail(emailToAdd.trim());
    });

    return;
  }

  addEmail(currentEmail);
}

function closeModal() {
  store.resetInvitations();
  email.value = null;

  Object.entries(modal).forEach(([ key ]) => {
    modal[key] = false;
  });
}

const isButtonDisabled = computed(
  () => emailsToInvite.length <= 0 || email.value || store.doesInvitationsHasError() || modal.isCheckingEmail,
);

const inputPlaceholder = computed(() => (emailsToInvite.length <= 0
  ? trans('user.organization.team.add_emails')
  : trans('user.organization.team.add_more_emails')));

function doDelete(index) {
  store.removeEmail(index);
  const hasError = store.doesInvitationsHasError();

  modal.isAlreadyMember = hasError;
  modal.isInActiveOrganization = hasError;
}

async function sendInvitations() {
  modal.isSubmitting = true;

  try {
    await store.sendInvitations();

    flashBagSuccess(trans('user.organization.team.email_sent'));
  } catch (e) {
    flashBagError();
    Logger.error('Error catched', e);
  } finally {
    modal.isSubmitting = false;
    closeModal();
  }
}

function getCurrentIndex() {
  if (Number.isInteger(tagSelectedIndex.value)) {
    return tagSelectedIndex.value;
  }

  return emailsToInvite.length > 0 ? emailsToInvite.length : null;
}

function selectPreviousTag() {
  if (email.value) {
    return;
  }

  const currentIndex = getCurrentIndex();

  if (!currentIndex) {
    return;
  }

  if (currentIndex > 0) {
    tagSelectedIndex.value = currentIndex - 1;
  }
}

function selectNextTag() {
  if (email.value) {
    return;
  }

  const currentIndex = getCurrentIndex();

  if (!Number.isInteger(currentIndex)) {
    return;
  }

  if (currentIndex < emailsToInvite.length - 1) {
    tagSelectedIndex.value = currentIndex + 1;
  }
}

function removeTag() {
  if (email.value) {
    return;
  }

  if (Number.isInteger(tagSelectedIndex.value)) {
    doDelete(tagSelectedIndex.value);
    tagSelectedIndex.value = null;
  } else {
    selectPreviousTag();
  }
}

function handleInput(event) {
  if (event.data === ' ') {
    onEmailAdd();
  }
}

function setFocus(isFocused) {
  input.isFocus = isFocused;

  if (!input.isFocus && email.value) {
    onEmailAdd();
  }
}

defineExpose({
  openModal,
});
</script>

<template>
  <slidein :is-opened="modal.isOpened" @close="closeModal">
    <template #header>
      {{ trans('user.organization.team.invite_member') }}
    </template>
    <template #body>
      <div class="invitation-modal">
        <div>
          <label for="emails">{{ trans('user.organization.team.add_emails_label') }}</label>

          <div
            class="invitation-modal-container"
            :class="{ 'invitation-modal-focus': input.isFocus, 'invitation-modal-hover': input.isHovered }"
            @click.prevent="inputEmail.focus()"
            @mouseover="input.isHovered = true"
            @mouseleave="input.isHovered = false"
          >
            <div class="input-tags">
              <invited-email-tag
                v-for="(invitedEmail, index) in emailsToInvite"
                :key="invitedEmail.email"
                :email="invitedEmail.email"
                :has-error="invitedEmail.hasError"
                :selected="index === tagSelectedIndex"
                @click="doDelete(index)"
              />
              <input
                id="email"
                ref="inputEmail"
                v-model="email"
                type="email"
                :placeholder="inputPlaceholder"
                @keyup.enter="onEmailAdd"
                @keyup.delete="removeTag"
                @input="event => handleInput(event)"
                @keyup.left="selectPreviousTag"
                @keyup.right="selectNextTag"
                @mouseover="input.isHovered = true"
                @mouseleave="input.isHovered = false"
                @focus="setFocus(true)"
                @blur="setFocus(false)"
              >
            </div>
          </div>
          <div class="errors">
            <p v-show="modal.isAlreadyMember">
              {{ trans('user.organization.team.member_is_already_present') }}
            </p>
            <div v-show="modal.isInActiveOrganization && !modal.isAlreadyMember">
              <p>
                {{ trans('user.organization.team.member_is_active', { company_name: $companyName }) }}
              </p>

              <account-manager-component :account-manager="accountManager" :display-title="false" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="invitation-modal-footer">
        <button class="editor-submit btn btn--secondary" @click.prevent="closeModal">
          {{ trans('global.cancel') }}
        </button>
        <button
          class="editor-submit btn btn--primary"
          :class="{ 'loading-btn': modal.isSubmitting || modal.isCheckingEmail }"
          :disabled="isButtonDisabled || modal.isSubmitting"
          @click.prevent="sendInvitations"
        >
          {{ trans('global.validate') }}
        </button>
      </div>
    </template>
  </slidein>
</template>
